import { Injectable, Injector, OnDestroy, inject } from '@angular/core';
import {
  ExternalLogEntryBatchCreateDtoV2,
  ExternalLogEntryCreateDtoV2,
  LogsServiceV2,
} from 'api-client';

import { CorrelationIdService } from './correlation-id.service';
import { DEFAULT_LOG_CONFIG, LOG_CONFIG, LogConfig } from './log-config';

/**
 * Service for buffering log entries and sending them in batches to the V2 Logs API.
 *
 * Features:
 * - Buffers log entries in memory to reduce API calls
 * - Flushes automatically on timer, buffer full, or critical log levels
 * - Uses sendBeacon for best-effort delivery on page unload
 * - Handles network errors gracefully with retry logic
 */
@Injectable({
  providedIn: 'root',
})
export class LogBufferService implements OnDestroy {
  private _injector = inject(Injector);
  private _correlationIdService = inject(CorrelationIdService);

  private _buffer: ExternalLogEntryCreateDtoV2[] = [];
  private _flushTimer: ReturnType<typeof setInterval> | null = null;
  private _config: LogConfig = DEFAULT_LOG_CONFIG;
  private _isInitialized = false;
  private _logsService: LogsServiceV2 | null = null;

  /**
   * Initialize the buffer service with configuration.
   * Called lazily on first use to allow for configuration injection.
   */
  private ensureInitialized(): void {
    if (this._isInitialized) return;

    try {
      this._config = this._injector.get(LOG_CONFIG) || DEFAULT_LOG_CONFIG;
    } catch {
      // Use default config if not provided
      this._config = DEFAULT_LOG_CONFIG;
    }

    // Only initialize if V2 logging is enabled
    if (this._config.enableV2Logging) {
      try {
        this._logsService = this._injector.get(LogsServiceV2);
        this.startFlushTimer();
        this.setupPageUnloadHandler();
      } catch {
        // LogsServiceV2 not available - V2 logging will be disabled
        // eslint-disable-next-line no-console -- Intentional: logging service bootstrap, cannot use LogService
        console.warn('[LogBufferService] LogsServiceV2 not available, V2 logging disabled');
        this._config = { ...this._config, enableV2Logging: false };
      }
    }

    this._isInitialized = true;
  }

  /**
   * Add a log entry to the buffer.
   * Triggers immediate flush for Error and Fatal levels.
   *
   * @param entry Log entry to add
   */
  add(entry: ExternalLogEntryCreateDtoV2): void {
    this.ensureInitialized();

    if (!this._config.enableV2Logging) {
      return;
    }

    this._buffer.push(entry);

    // Immediate flush for critical log levels
    if (entry.level === 'Error' || entry.level === 'Fatal') {
      this.flush();
    } else if (this._buffer.length >= (this._config.bufferSize || 25)) {
      // Buffer full - flush
      this.flush();
    }
  }

  /**
   * Flush all buffered log entries to the API.
   * Entries are removed from buffer only after successful submission.
   */
  flush(): void {
    if (this._buffer.length === 0 || !this._logsService) {
      return;
    }

    const entriesToSend = [...this._buffer];
    this._buffer = [];

    const batch: ExternalLogEntryBatchCreateDtoV2 = {
      correlationId: this._correlationIdService.getCorrelationId(),
      source: this._config.source,
      entries: entriesToSend,
    };

    this._logsService.apiV2LogsEntriesPost(batch).subscribe({
      next: () => {
        // Successfully sent - entries already removed from buffer
      },
      error: (error) => {
        // On failure, add entries back to buffer for retry
        // But limit retries by only adding back once
        // eslint-disable-next-line no-console -- Intentional: logging service infrastructure, cannot use LogService
        console.error('[LogBufferService] Failed to send logs, will retry:', error);
        this._buffer.unshift(...entriesToSend.filter((e) => !e.sourceContext?.includes('[RETRY]')));
      },
    });
  }

  /**
   * Get the current buffer size.
   * Useful for debugging and monitoring.
   */
  getBufferSize(): number {
    return this._buffer.length;
  }

  /**
   * Check if V2 logging is enabled.
   */
  isV2LoggingEnabled(): boolean {
    this.ensureInitialized();
    return this._config.enableV2Logging;
  }

  /**
   * Start the periodic flush timer.
   */
  private startFlushTimer(): void {
    if (this._flushTimer) {
      clearInterval(this._flushTimer);
    }

    const intervalMs = this._config.flushIntervalMs || 5000;
    this._flushTimer = setInterval(() => {
      this.flush();
    }, intervalMs);
  }

  /**
   * Set up page unload handler for best-effort log delivery.
   * Uses sendBeacon API for reliable delivery during page close.
   */
  private setupPageUnloadHandler(): void {
    if (typeof window === 'undefined') {
      return; // Not in browser environment
    }

    // Use beforeunload for synchronous flush attempt
    window.addEventListener('beforeunload', () => {
      this.flushWithBeacon();
    });

    // Also handle visibility change for mobile browsers
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flushWithBeacon();
      }
    });
  }

  /**
   * Flush logs using sendBeacon for page unload scenarios.
   * sendBeacon is more reliable than fetch during page close.
   */
  private flushWithBeacon(): void {
    if (this._buffer.length === 0) {
      return;
    }

    const batch: ExternalLogEntryBatchCreateDtoV2 = {
      correlationId: this._correlationIdService.getCorrelationId(),
      source: this._config.source,
      entries: this._buffer,
    };

    // Use sendBeacon for reliable delivery during page unload
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(batch)], { type: 'application/json' });
      navigator.sendBeacon('/api/v2/logs/entries', blob);
    }

    // Clear buffer after beacon attempt (best effort)
    this._buffer = [];
  }

  ngOnDestroy(): void {
    if (this._flushTimer) {
      clearInterval(this._flushTimer);
      this._flushTimer = null;
    }

    // Final flush on service destruction
    this.flush();
  }
}
